/**
 * useWakeBrief — fetches a wake-brief on mount if one is due.
 *
 * `lastSeen` defaults to a localStorage key namespaced by session_id; if
 * the operator has been gone long enough per backend §9.5, returns the
 * brief. 404 (no brief due) resolves to `brief = null`.
 */
import { useCallback, useEffect, useState } from 'react';

import type { WakeBriefPayload } from '../types/council';
import { councilApiClient } from '../services/councilApiClient';

const LAST_SEEN_KEY = (sessionId: string) => `council.lastSeen.${sessionId}`;

export function useWakeBrief(sessionId: string) {
  const [brief, setBrief] = useState<WakeBriefPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const lastSeenIso =
      localStorage.getItem(LAST_SEEN_KEY(sessionId))
      ?? new Date().toISOString();
    setLoading(true);
    setError(null);
    councilApiClient.getWakeBrief(sessionId, lastSeenIso)
      .then(result => { if (!cancelled) setBrief(result); })
      .catch(e => { if (!cancelled) setError(e as Error); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId]);

  const dismiss = useCallback(async () => {
    if (!brief || !brief.id) return;
    try {
      await councilApiClient.markBriefRead(sessionId, brief.id);
      setBrief(null);
      localStorage.setItem(LAST_SEEN_KEY(sessionId), new Date().toISOString());
    } catch (e) {
      setError(e as Error);
    }
  }, [brief, sessionId]);

  return { brief, loading, error, dismiss };
}
